import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { vacationReceiptService } from '../../services/vacationReceiptService';
import type { CreateReceiptInput } from '../../services/vacationReceiptService';
import type { VacationReceipt } from '../../models/vacationReceipt.types';
import { VACATION_RECEIPTS_QUERY_KEY } from './useVacationReceiptsQuery';

export type UploadReceiptInput = Omit<CreateReceiptInput, 'onProgress'>;

export function useVacationReceiptMutations(collaboratorId?: string) {
    const queryClient = useQueryClient();
    const toast = useToast();

    const [uploadProgress, setUploadProgress] = useState(0);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const invalidate = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: [VACATION_RECEIPTS_QUERY_KEY, collaboratorId],
            }),
        [queryClient, collaboratorId]
    );

    const reportError = (error: Error, title: string) => {
        console.error(title, error);
        toast.error(title, error.message || 'Verifique sua conexão e tente novamente.');
    };

    const uploadReceipt = useMutation({
        retry: false,
        mutationFn: (input: UploadReceiptInput) => {
            setUploadProgress(0);
            return vacationReceiptService.create({ ...input, onProgress: setUploadProgress });
        },
        onSuccess: () => {
            invalidate();
            toast.success('Recibo anexado', 'O documento já aparece na lista de recibos.');
        },
        onError: (error: Error) => reportError(error, 'Não foi possível anexar o recibo'),
        onSettled: () => setUploadProgress(0),
    });

    const deleteReceipt = useMutation({
        retry: false,
        mutationFn: (receipt: VacationReceipt) => {
            setDeletingId(receipt.id);
            return vacationReceiptService.remove(receipt);
        },
        onSuccess: () => {
            invalidate();
            toast.success('Recibo excluído');
        },
        onError: (error: Error) => reportError(error, 'Não foi possível excluir o recibo'),
        onSettled: () => setDeletingId(null),
    });

    return { uploadReceipt, deleteReceipt, uploadProgress, deletingId };
}
